"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Pattern and walker for RNG's ``attribute`` elements.
 * @author Louis-Dominique Dubeau
 * @license MPL 2.0
 * @copyright Mangalam Research Center for Buddhist Languages
 */
const errors_1 = require("../errors");
const set_1 = require("../set");
const base_1 = require("./base");
/**
 * A pattern for attributes.
 */
class Attribute extends base_1.Pattern {
    /**
     * @param xmlPath This is a string which uniquely identifies the
     * element from the simplified RNG tree. Used in debugging.
     *
     * @param name The qualified name of the attribute.
     *
     * @param pat The pattern contained by this one.
     */
    constructor(xmlPath, name, pat) {
        super(xmlPath);
        this.name = name;
        this.pat = pat;
    }
    _prepare(definitions, namespaces) {
        const ret = this.pat._prepare(definitions, namespaces);
        this.name._recordNamespaces(namespaces, false);
        return ret;
    }
    hasAttrs() {
        return true;
    }
    hasEmptyPattern() {
        return false;
    }
    newWalker() {
        // tslint:disable-next-line:no-use-before-declare
        return new AttributeWalker(this, this.pat.newWalker(), false, /* seenName */ false, false);
    }
}
exports.Attribute = Attribute;
/**
 * Walker for [[Attribute]].
 */
class AttributeWalker {
    /**
     * @param el The pattern for which this walker was created.
     */
    constructor(el, subwalker, seenName, canEndAttribute, canEnd) {
        this.el = el;
        this.subwalker = subwalker;
        this.seenName = seenName;
        this.canEndAttribute = canEndAttribute;
        this.canEnd = canEnd;
        this.name = el.name;
    }
    clone() {
        return new AttributeWalker(this.el, this.subwalker.clone(), this.seenName, this.canEndAttribute, this.canEnd);
    }
    possible() {
        return new Set();
    }
    possibleAttributes() {
        if (this.canEnd) {
            return new Set();
        }
        if (!this.seenName) {
            return new Set([new base_1.Event("attributeName", this.name)]);
        }
        // Convert text events to attributeValue events.
        return set_1.map(this.subwalker.possible(), (ev) => {
            if (ev.params[0] !== "text") {
                throw new Error(`unexpected event type: ${ev.params[0]}`);
            }
            return new base_1.Event("attributeValue", ev.params[1]);
        });
    }
    fireEvent(name, params, nameResolver) {
        // If canEnd is true, we've done everything we could. So we don't
        // want to match again.
        if (this.canEnd) {
            return new base_1.InternalFireEventResult(false);
        }
        let value;
        if (this.seenName) {
            if (name !== "attributeValue") {
                return new base_1.InternalFireEventResult(false);
            }
            value = params[0];
        }
        else if ((name === "attributeName" || name === "attributeNameAndValue") &&
            this.name.match(params[0], params[1])) {
            this.seenName = true;
            if (name === "attributeName") {
                return new base_1.InternalFireEventResult(true);
            }
            value = params[2];
        }
        else {
            return new base_1.InternalFireEventResult(false);
        }
        this.canEnd = true;
        this.canEndAttribute = true;
        if (value !== "" &&
            !this.subwalker.fireEvent("text", [value], nameResolver).matched) {
            return new base_1.InternalFireEventResult(false, [new errors_1.AttributeValueError("invalid attribute value", this.name)]);
        }
        return base_1.InternalFireEventResult.fromEndResult(this.subwalker.end());
    }
    endAttributes() {
        if (this.canEnd) {
            return false;
        }
        // We set the canEnd flags true even though we did not end properly. This
        // prevents producing errors about the same attribute multiple times,
        // because end is called by element walkers when leaveStartTag is
        // encountered, and again when the element closes.
        this.canEnd = true;
        this.canEndAttribute = true;
        return [this.seenName ?
                new errors_1.AttributeValueError("attribute value missing", this.name) :
                new errors_1.AttributeNameError("attribute missing", this.name)];
    }
    end() {
        return false;
    }
}
//  LocalWords:  RNG's MPL RNG attributeName attributeValue ev params
//  LocalWords:  neutralizeAttribute
//# sourceMappingURL=attribute.js.map